const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

interface RequestConfig {
  method?: string;
  headers?: Record<string, string>;
  body?: any;
  skipRetry?: boolean;
}

class ApiClient {
  private baseUrl: string;
  private refreshPromise: Promise<string | null> | null = null;
  private onUnauthorized: (() => void) | null = null;
  defaults: {
    headers: {
      common: Record<string, string>;
    };
  };

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.defaults = {
      headers: {
        common: {},
      },
    };
  }

  setOnUnauthorized(callback: () => void) {
    this.onUnauthorized = callback;
  }

  private async tryRefreshToken(): Promise<string | null> {
    // If already refreshing, wait for that to complete
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        // Determine which refresh endpoint to use based on current user role
        const token = localStorage.getItem('accessToken');
        let refreshEndpoint = '/auth/admin/refresh';

        if (token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            if (payload.role === 'STORE_ADMIN') {
              refreshEndpoint = '/auth/store/refresh';
            }
          } catch {
            // If we can't decode the token, try admin refresh first
          }
        }

        const response = await fetch(`${this.baseUrl}${refreshEndpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Refresh failed');
        }

        const data = await response.json();
        const newToken = data.data.token;

        // Update stored token
        localStorage.setItem('accessToken', newToken);
        this.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

        return newToken;
      } catch (error) {
        // Refresh failed - clear auth state
        localStorage.removeItem('accessToken');
        delete this.defaults.headers.common['Authorization'];
        return null;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  private async request<T>(endpoint: string, config: RequestConfig = {}): Promise<{ data: T }> {
    const { method = 'GET', headers = {}, body, skipRetry = false } = config;

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...this.defaults.headers.common,
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'include',
    });

    // Handle 401 - try to refresh token
    if (response.status === 401 && !skipRetry) {
      const newToken = await this.tryRefreshToken();

      if (newToken) {
        // Retry the original request with new token
        const retryResponse = await fetch(`${this.baseUrl}${endpoint}`, {
          method,
          headers: {
            'Content-Type': 'application/json',
            ...this.defaults.headers.common,
            ...headers,
            'Authorization': `Bearer ${newToken}`,
          },
          body: body ? JSON.stringify(body) : undefined,
          credentials: 'include',
        });

        if (retryResponse.ok) {
          const data = await retryResponse.json();
          return { data };
        }
      }

      // Token refresh failed or retry failed - trigger logout
      if (this.onUnauthorized) {
        this.onUnauthorized();
      }
      throw new Error('Session expired. Please log in again.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    const data = await response.json();
    return { data };
  }

  get<T>(endpoint: string) {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  post<T>(endpoint: string, body?: any) {
    return this.request<T>(endpoint, { method: 'POST', body });
  }

  put<T>(endpoint: string, body?: any) {
    return this.request<T>(endpoint, { method: 'PUT', body });
  }

  delete<T>(endpoint: string) {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async getBlob(endpoint: string): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'GET',
      headers: {
        ...this.defaults.headers.common,
      },
      credentials: 'include',
    });

    // Handle 401 - try to refresh token
    if (response.status === 401) {
      const newToken = await this.tryRefreshToken();

      if (newToken) {
        const retryResponse = await fetch(`${this.baseUrl}${endpoint}`, {
          method: 'GET',
          headers: {
            ...this.defaults.headers.common,
            'Authorization': `Bearer ${newToken}`,
          },
          credentials: 'include',
        });

        if (retryResponse.ok) {
          return retryResponse.blob();
        }
      }

      if (this.onUnauthorized) {
        this.onUnauthorized();
      }
      throw new Error('Session expired. Please log in again.');
    }

    if (!response.ok) {
      throw new Error('Failed to download file');
    }

    return response.blob();
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
